import { CircularProgress, Stack, Typography } from "@mui/material";
import { useTransactionsDetail } from "Api/Queries/Transactions/useTransactionsDetail";
import { useTransactionStatus } from "Api/Queries/Transactions/useTransactionStatus";
import { useTransactionStatusSignalR } from "Hooks/Transactions/useTransactionStatusSignalR";
import { useEffect } from "react";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { Resources, useResource } from "Translations/Resources";
import { useIsMobile } from "Hooks/useIsMobile";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";
import { TransactionStatus } from "Infrastructure/Api/Api";
import { PaymentInProgressInfo } from "Components/Merchant/InitiatePaymentDetail/PaymentInProgress/PaymentInProgressInfo";
import { SuccessfulPaymentInfo } from "Components/Merchant/InitiatePaymentDetail/SuccessfulPayment/SuccessfulPaymentInfo";

const RESOURCES = Resources.Merchant.InitiateDetail;

type Props = {
  transactionPublicID: string;
};

export const PaymentDetail = ({ transactionPublicID }: Props) => {
  const { t } = useResource();
  const isMobile = useIsMobile();
  const isMerchant = useIsCurrentUserMerchant();

  const { data: transactionsDetail, isLoading: isLoadingTransactionsDetail } =
    useTransactionsDetail({ publicID: transactionPublicID });
  const { data: status, isLoading: isLoadingStatus } =
    useTransactionStatus(transactionPublicID);
  useTransactionStatusSignalR({ publicID: transactionPublicID });

  useEffect(() => {
    if (transactionsDetail?.signingUrl) {
      window.location.href = transactionsDetail.signingUrl;
    }
  }, [transactionsDetail?.signingUrl]);

  if (isLoadingTransactionsDetail || isLoadingStatus) {
    return (
      <Stack
        height={isMobile ? "100%" : "740px"}
        width="100%"
        justifyContent="center"
        alignItems="center"
        gap={3}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          {t(RESOURCES.Loading)}
        </Typography>
      </Stack>
    );
  }

  if (!transactionsDetail || !status) {
    return <ErrorText>{t(RESOURCES.Error)}</ErrorText>;
  }

  const {
    currencyCode,
    amount,
    publicID,
    creditorName,
    payPointPublicID,
    merchantIdentityUserPublicID,
    merchantProfilePicture,
  } = transactionsDetail;
  const transactionStatus = status.transactionStatus;

  if (transactionStatus === TransactionStatus.Success) {
    return (
      <SuccessfulPaymentInfo
        amount={amount}
        currencyCode={currencyCode}
        transactionPublicID={publicID}
        creditorName={creditorName}
        payPointPublicID={payPointPublicID}
        merchantIdentityUserPublicID={merchantIdentityUserPublicID}
        merchantProfilePicture={merchantProfilePicture}
      />
    );
  }

  return (
    <PaymentInProgressInfo
      isMerchant={isMerchant}
      transactionStatus={transactionStatus}
      transactionsDetail={transactionsDetail}
    />
  );
};
